(ns app.front.ui.chat-panel
  "Floating chat panel with bot conversation interface."
  (:require ["@carbon/icons-react/es/send--alt/24" :default SendAlt24]
            [app.domain.group.model :as group-model]
            [app.freshdesk.model :as freshdesk]
            [app.front.entities :as entities]
            [app.front.form.subs :as form-subs]
            [app.front.guide-context :as guide-context]
            [app.front.process.save-tracking :as save-tracking]
            [app.front.ui :as ui]
            [app.front.ui.dom :as dom]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.panels :as panels]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.side-panel :as side-panel]
            [app.front.util :as futil]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.http]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def client-id-storage-key "ai-chat-id")

;; Backend chat memory lives in GCS with a 3-day (72h) deletion rule, so a session id
;; must not be reused beyond 72 hours from the moment it was created. See WIN-5524.
(def client-id-ttl-ms (* 72 60 60 1000))

(defn- valid-stored-client-id
  "Reads the persisted chat session id from localStorage.

   Returns the id only when it carries a `:created-at` timestamp newer than
   `client-id-ttl-ms`. Expired ids and legacy bare-string values (which lack a
   timestamp) are treated as expired: the key is removed and nil is returned."
  []
  (let [{:strs [id created-at]} (try
                                  (futil/local-storage-get-json client-id-storage-key)
                                  (catch :default _ nil))]
    (if (and id created-at
             (< (- (js/Date.now) created-at) client-id-ttl-ms))
      id
      (do
        (futil/local-storage-set! client-id-storage-key nil)
        nil))))

;; Panel state
(def default-width 384)

(def ^:private public-ai-chat-routes
  #{:view/landing-page
    :view/public-records
    :view/create-complaint
    :view/create-contact-request})

(rf/reg-sub ::ai-disabled-for-group?
  :<- [::entities/context]
  :<- [:branding]
  (fn [[ctx brand] _]
    (let [branded-group (when brand
                          (entities/find-entity ctx {:group/uri-path-id brand
                                                     :group/type        :group.type/domain}))]
      (group-model/ai-disabled? branded-group))))

;; Is AI chat available? Requires ai-chat feature flag AND (logged-in user OR public route).
(rf/reg-sub ::chat-enabled?
  :<- [::ff/enabled? :feature-flag.id/ai-chat]
  :<- [:session/logged-in?]
  :<- [:route/match]
  :<- [::ai-disabled-for-group?]
  (fn [[ai-chat-ff-enabled? logged-in? route-match ai-disabled-for-group?] _]
    (and ai-chat-ff-enabled?
         (not ai-disabled-for-group?)
         (or logged-in?
             (contains? public-ai-chat-routes (-> route-match :data :name))))))

(rf/reg-sub ::panel-open?
  :<- [::panels/open? :chat]
  :-> boolean)

(rf/reg-sub ::panel-width
  (fn [db _]
    (or (::panel-width db) default-width)))

(rf/reg-event-db ::set-panel-width
  (fn [db [_ width]]
    (assoc db ::panel-width width)))

(rf/reg-event-fx ::toggle-open
  (fn [{:keys [db]} [_ {:keys [open? on-success]}]]
    (let [client-id (valid-stored-client-id)
          fx        (concat [[:dispatch [::panels/toggle :chat]]]
                            (when (and on-success (nil? client-id))
                              [[:dispatch on-success]]))]
      (merge (util/mmap fx)
             (when open?
               (if client-id
                 {:db           (assoc db ::client-id client-id ::history-loading? true)
                  :comm/command {:command          :workspace/chat-history
                                 :method           :get
                                 :data             {:client-id client-id}
                                 :on-success       [::history-loaded on-success]
                                 :finally-dispatch [::history-load.done]}}
                 {:db (assoc db ::client-id nil ::messages nil ::chat-version nil)}))))))

(rf/reg-event-fx ::history-loaded
  (fn [{:keys [db]} [_ on-success _status {:keys [messages version]}]]
    (p/assoc-if {:db (assoc db
                            ::messages (when (seq messages)
                                         (vec messages))
                            ::chat-version version)}
                :dispatch on-success)))

(rf/reg-event-db ::history-load.done
  (fn [db _]
    (dissoc db ::history-loading?)))

(rf/reg-event-fx ::reset-chat
  (fn [{:keys [db]} _]
    (when-let [close-es (::close-event-source db)]
      (close-es))
    {:db                    (dissoc db ::client-id ::chat-version ::messages ::message
                                    ::close-event-source ::loading? ::history-loading?
                                    ::latest-proposal-id ::ticket-edit-modal ::ticket-creation-state
                                    ::ticket-creation-failed?)
     :local-storage-remove! [client-id-storage-key]}))

(rf/reg-event-db ::set-message
  (fn [db [_ value]]
    (assoc db ::message value)))

;; ── Guide highlight (NoVa chat → page element) ────────────────

(defn- guide-highlight-selector [escaped-id]
  (let [root-selector      (str "[data-guide-id=\"" escaped-id "\"]")
        root-el            (.querySelector (.-body js/document) root-selector)
        highlight-selector (some-> root-el
                                   (.getAttribute "data-guide-highlight-selector")
                                   str/trim
                                   not-empty)
        child-matches?     (some->> highlight-selector (.querySelector root-el))]
    (if child-matches?
      ;; May be a comma-separated selector list — scope each part under `root-selector`
      ;; individually, or an unscoped part like `textarea` would match the whole page.
      (->> (str/split highlight-selector #",")
           (map #(str root-selector " " (str/trim %)))
           (str/join ", "))
      root-selector)))

(rf/reg-fx ::highlight
  (fn [element-id]
    (let [escaped-id (some-> js/CSS (.escape element-id))]
      (dom/set-head-style! "guide-highlight-style"
                           (when escaped-id
                             (str "@media (min-width: 1024px) { "
                                  (guide-highlight-selector escaped-id) " { "
                                  "box-shadow: var(--guide-highlight-box-shadow); "
                                  "border-radius: var(--guide-highlight-border-radius); } }"))))))

(rf/reg-event-fx ::highlight
  (fn [_ [_ id]] {::highlight id}))

(rf/reg-event-fx ::clear-highlight
  (fn [_ _] {::highlight nil}))

(defn- build-page-context
  "Assembles the page-context map from route state and guide data."
  [{:keys [route-name workspace-id view-path guide-context-enabled? guide-elements page-description forms-context]}]
  (when (ff/enabled? :feature-flag.id/ai-chat-context-aware)
    (cond-> {:route-name route-name
             :url        (str js/window.location.href)}
      workspace-id (assoc :workspace-id workspace-id
                          :view-path    view-path)
      (and guide-context-enabled? (not-empty page-description)) (assoc :page-description page-description)
      (and guide-context-enabled? (seq guide-elements))         (assoc :guide-elements guide-elements)
      (and forms-context (ff/enabled? :feature-flag.id/nova-form-filler)) (merge forms-context))))

(defn- prepend-context-changed-note [{:keys [route-name guide-elements]} message]
  (let [guide-labels (not-empty (keep :label guide-elements))]
    (str "[System note: The page context has changed. The user is now on the "
         (or (some-> route-name name) "current") " view. "
         (if guide-labels
           (str "Key UI elements visible: " (str/join ", " guide-labels) ". ")
           "No key UI elements are currently visible. ")
         "Do not reference previous page elements that are no longer present.]\n\n"
         message)))

(defn- prepend-form-context-changed-note [{:keys [expanded-form-id forms]} message]
  (let [form-name (some #(when (= (:form-id %) expanded-form-id) (:name %)) forms)]
    (str "[System note: The open form has changed since the previous message. "
         (if expanded-form-id
           (if form-name
             (str "The currently open form is now '" form-name "' (id " expanded-form-id "). ")
             (str "The currently open form has id " expanded-form-id
                  ", but it is missing from the current form list. "))
           "No form is open now. ")
         "Forms discussed earlier are closed and can no longer be filled. When the user refers to "
         "'this form', they mean the currently open one — call GetFormFields with the current form-id "
         "instead of relying on fields discussed earlier.]\n\n"
         message)))

(defn- enrich-message
  "Prepends system notes to the user message when context has changed or ticket failed."
  [message {:keys [ticket-failed? context-changed? form-context-changed? forms-context route-name guide-elements]}]
  (cond->> message
    ticket-failed?
    (str "[System note: The user previously attempted to create the support ticket you proposed, "
         "but the creation failed due to a server error. The ticket was not created or sent. "
         "Acknowledge the failure briefly. Do not suggest generic troubleshooting such as refreshing the page. "
         "Do not create a new ticket draft yet and do not call the proposeTicket tool unless the user explicitly asks you to prepare the ticket again. "
         "Ask whether the user wants to prepare a new draft ticket or try creating the same ticket again.]\n\n")

    context-changed?
    (prepend-context-changed-note {:route-name     route-name
                                   :guide-elements guide-elements})

    form-context-changed?
    (prepend-form-context-changed-note forms-context)))

(defn- changed-since-last? [db signature-key current-signature]
  (and current-signature
       (get db signature-key)
       (not= current-signature (get db signature-key))))

(rf/reg-event-fx ::send-message
  (fn [{:keys [db]} [_ message forms-context]]
    (let [valid-client-id        (valid-stored-client-id)
          fresh-session?         (nil? valid-client-id)
          client-id              (or valid-client-id (str (random-uuid)))
          base-db                (cond-> db
                                   fresh-session? (dissoc ::messages ::chat-version ::latest-proposal-id
                                                          ::ticket-creation-failed? ::ticket-creation-state
                                                          ::ticket-edit-modal ::last-guide-context-signature
                                                          ::last-form-context-signature))
          chat-version           (::chat-version base-db)
          ticket-failed?         (::ticket-creation-failed? base-db)
          guide-context-enabled? (ff/enabled? :feature-flag.id/ai-chat-guide-context)

          route-match      (:route/match db)
          route-name       (get-in route-match [:data :name])
          workspace-id     (get-in db [:view/workspace :workspace/id])
          view-path        (when workspace-id
                             (str/replace (str (:template route-match)) #"^/workspace/:[^/]+" ""))
          guide-elements   (when guide-context-enabled?
                             (guide-context/collect-guide-elements))
          page-description (when guide-context-enabled?
                             (guide-context/collect-guide-page-description))

          current-signature (when guide-context-enabled?
                              [page-description
                               (->> guide-elements
                                    (map #(select-keys % [:id :label :description]))
                                    (sort-by :id)
                                    vec)])
          context-changed?  (and guide-context-enabled?
                                 (changed-since-last? base-db ::last-guide-context-signature current-signature))

          ;; Wrapped in a vector so that "no form open" ([nil]) is distinguishable from
          ;; "not on the forms step at all" (nil, signature not tracked).
          form-signature        (when (and forms-context (ff/enabled? :feature-flag.id/nova-form-filler))
                                  [(:expanded-form-id forms-context)])
          form-context-changed? (changed-since-last? base-db ::last-form-context-signature form-signature)

          enriched-msg (enrich-message message {:ticket-failed?        ticket-failed?
                                                :context-changed?      context-changed?
                                                :form-context-changed? form-context-changed?
                                                :forms-context         forms-context
                                                :route-name            route-name
                                                :guide-elements        guide-elements})
          page-context (build-page-context {:route-name             route-name
                                            :workspace-id           workspace-id
                                            :view-path              view-path
                                            :guide-context-enabled? guide-context-enabled?
                                            :guide-elements         guide-elements
                                            :page-description       page-description
                                            :forms-context          forms-context})
          close-es     (comm/fetch-event-source {:url             (core.http/command-name->path :workspace/chat-interface)
                                                 :method          :post
                                                 :event-name      "chat-token"
                                                 :data            (p/assoc-if
                                                                    {:message   enriched-msg
                                                                     :client-id client-id}
                                                                    :brand        (:branding db)
                                                                    :chat-version chat-version
                                                                    :page-context page-context)
                                                 :on-message      (fn [data]
                                                                    (rf/dispatch-sync [::message-received data]))
                                                 :on-error        (fn [err]
                                                                    (rf/dispatch-sync [::message-error err]))
                                                 :close-on-error? true})]
      (p/assoc-if
        {:db (-> (update base-db ::messages (fnil conj []) {:text      message
                                                            :timestamp (js/Date.)
                                                            :sender    :user})
                 (assoc ::client-id client-id ::message "" ::loading? true ::close-event-source close-es)
                 (p/assoc-if ::last-guide-context-signature current-signature
                             ::last-form-context-signature form-signature)
                 (cond-> ticket-failed? (-> (dissoc ::latest-proposal-id)
                                            (dissoc ::ticket-creation-failed?))))}
        :local-storage-set-json! (when fresh-session?
                                   [[client-id-storage-key {:id client-id :created-at (js/Date.now)}]])))))

(rf/reg-event-fx ::message-received
  (fn [{:keys [db]} [_ {:keys [id text final? version actions]}]]
    (let [messages          (::messages db)
          actions-by-type   (group-by :type actions)
          ticket-actions    (:propose-ticket actions-by-type)
          form-fill-actions (:form-fields-updated actions-by-type)
          new-messages      (if-let [ix (p/find-index messages {:id id})]
                              (cond-> (if final?
                                        (-> (assoc-in messages [ix :final?] true)
                                            (assoc-in [ix :text] text))
                                        (update-in messages [ix :text] str text))
                                (seq ticket-actions) (update-in [ix :actions] (fnil into []) ticket-actions))
                              (conj (or messages [])
                                    (cond-> {:text      text
                                             :id        id
                                             :timestamp (js/Date.)
                                             :sender    :bot}
                                      final?               (assoc :final? true)
                                      (seq ticket-actions) (assoc :actions ticket-actions))))]
      {:db (cond-> (assoc db ::messages new-messages)
             (and final? version) (assoc ::chat-version version)
             (seq ticket-actions) (assoc ::latest-proposal-id id
                                         ::ticket-creation-state :idle)
             text                 (assoc ::loading? false))
       :fx (mapv (fn [action] [:dispatch [::apply-form-field-updates action]]) form-fill-actions)})))

(rf/reg-event-fx ::apply-form-field-updates
  ;; No highlight fx here: the changed field is referenced via a <span data-guide="..."> in
  ;; NoVa's response instead. Generation bump must be dispatched last so the remount picks up
  ;; the new entity-db values.
  (fn [{:keys [db]} [_ {:keys [form-id entity-changes]}]]
    {:fx (cond-> (-> (into [[:dispatch [::form-subs/capture-opening-values form-id]]]
                           (map (fn [changes] [:dispatch [::entities/apply-changes 200 changes]]) entity-changes))
                     (conj [:dispatch [::form-subs/refresh-form-field-info form-id]])
                     (conj [:dispatch [::form-subs/bump-ai-fill-generation]]))
           ;; Flash "saved" only on the wizard forms step, where NoVa's field fills are the save.
           (and (save-tracking/tracking-enabled? db)
                (= "forms" (get-in db [:route/match :parameters :path :step])))
           (conj [:dispatch [::save-tracking/record-external-save]]))}))

(rf/reg-event-fx ::message-error
  (fn [{:keys [db]} [_ err]]
    (let [messages     (::messages db)
          new-messages (conj (or messages [])
                             {:error     (if (= (some-> err .-response .-status) 429)
                                           :error/rate-limit-exceeded
                                           :error/something-went-wrong)
                              :id        "err"
                              :timestamp (js/Date.)
                              :sender    :bot})]
      {:db (-> db
               (assoc ::messages new-messages)
               (assoc ::loading? false))})))

(rf/reg-event-fx ::confirm-ticket
  (fn [{:keys [db]} [_ {:keys [ticket]}]]
    {:db           (assoc db ::ticket-creation-state :loading)
     :comm/command {:command          :workspace/create-ticket
                    :method           :post
                    :data             {:ticket ticket}
                    :notify           {:on-failure :chat/ticket-error}
                    :on-success       [::ticket-created]
                    :on-error         [::ticket-creation-failed]
                    :finally-dispatch [::ticket-creation-finished]}}))

(rf/reg-event-fx ::ticket-created
  (fn [{:keys [db]} [_ _status {:keys [ticket-id]} _request-id]]
    {:db (-> db
             (assoc ::ticket-creation-state :confirmed)
             (dissoc ::ticket-creation-failed?)
             (dissoc ::latest-proposal-id)
             (update ::messages (fnil conj [])
                     {:ticket-id ticket-id
                      :id        (str "ticket-" ticket-id)
                      :timestamp (js/Date.)
                      :sender    :bot}))}))

(rf/reg-event-fx ::ticket-creation-failed
  (fn [{:keys [db]} _]
    {:db (-> db
             (assoc ::ticket-creation-state :idle)
             (assoc ::ticket-creation-failed? true)
             (update ::messages (fnil conj [])
                     {:error     :chat/ticket-error
                      :id        (str "ticket-error-" (random-uuid))
                      :timestamp (js/Date.)
                      :sender    :bot}))}))

(rf/reg-event-fx ::ticket-creation-finished
  (fn [_ [_ status _body]]
    (when-not (= status 200)
      {:dispatch [::ticket-creation-failed]})))

(rf/reg-sub ::message :-> ::message)

(rf/reg-sub ::messages :-> ::messages)

(rf/reg-sub ::latest-proposal-id :-> ::latest-proposal-id)

(rf/reg-sub ::loading? :-> ::loading?)

(rf/reg-sub ::history-loading? :-> ::history-loading?)

(rf/reg-sub ::client-id :-> ::client-id)

(rf/reg-event-db ::open-ticket-edit-modal
  (fn [db [_ ticket]]
    (assoc db ::ticket-edit-modal (assoc ticket :open? true))))

(rf/reg-event-db ::close-ticket-edit-modal
  (fn [db [_]]
    (dissoc db ::ticket-edit-modal)))

(rf/reg-event-db ::set-ticket-edit-field
  (fn [db [_ field value]]
    (assoc-in db [::ticket-edit-modal field] value)))

(rf/reg-sub ::ticket-edit-ticket
  (fn [db _]
    (dissoc (get db ::ticket-edit-modal) :open?)))

(rf/reg-sub ::ticket-proposal-ui
  (fn [db [_ message-id]]
    (let [message   (p/find-first (::messages db) #(= (:id %) message-id))
          proposal  (some #(when (= (:type %) :propose-ticket) %) (:actions message))
          latest-id (::latest-proposal-id db)
          state     (or (::ticket-creation-state db) :idle)
          loading?  (::loading? db)]
      {:client-id  (::client-id db)
       :proposal   proposal
       :is-latest? (= message-id latest-id)
       :state      state
       :loading?   loading?
       :disabled?  (or (not= message-id latest-id)
                       (not= state :idle))})))

(rf/reg-sub ::ticket-edit-modal-open?
  (fn [db _]
    (:open? (::ticket-edit-modal db))))

;; Bot identity (hardcoded)
(def bot-name "NoVa")

(defn- message-key [{:keys [id sender timestamp text error ticket-id]}]
  (or id
      (str (name sender) ":" timestamp ":" (hash [text error ticket-id]))))

(defn ticket-edit-modal [tr]
  (when  (<sub [::ticket-edit-modal-open?])
    (let [{:keys [subject description] :as ticket} (<sub [::ticket-edit-ticket])
          client-id                                (<sub [::client-id])
          close-modal                              #(>evt [::close-ticket-edit-modal])
          on-confirm                               (fn [edited-ticket]
                                                     (close-modal)
                                                     (>evt [::confirm-ticket {:ticket    edited-ticket
                                                                              :client-id client-id}]))]
      [ui-modal/composed-input-modal
       {:open                   true
        :class-name             "modal-overflow-visible"
        :selector-primary-focus "#ticket-subject"
        :on-close               close-modal}
       (cc/modal-header
         {:close-modal close-modal
          :title       (tr [:chat/edit-ticket-title])})
       (cc/modal-body
         [:div.space-y-4
          [ui/text-input {:id         "ticket-subject"
                          :label-text (tr [:chat/ticket-subject])
                          :value      subject
                          :on-change  #(>evt [::set-ticket-edit-field :subject (futil/tval %)])}]
          [ui-rt/editor {:id            "ticket-description"
                         :label-text    (tr [:chat/ticket-description])
                         :initial-value description
                         :on-change     #(>evt [::set-ticket-edit-field :description %])}]
          [:div.flex.gap-4
           (futil/for-frag [[field items] {"type"         freshdesk/ticket-type
                                           "product-type" freshdesk/product-type
                                           "feature"      freshdesk/related-feature-functionality}]
             (let [k    (keyword "chat" (str "ticket-" field))
                   item ((keyword field) ticket)]
               [:div.flex-1
                (cc/dropdown
                  {:id            field
                   :label         (tr [k])
                   :title-text    (tr [k])
                   :selected-item item
                   :items         items
                   :on-change     #(>evt [::set-ticket-edit-field (keyword field) (futil/oget % :selectedItem)])})]))]])
       (cc/modal-footer
         {:primary-button-text   (tr [:ok])
          :secondary-button-text (tr [:cancel])
          :on-request-submit     #(on-confirm ticket)
          :on-request-close      close-modal})])))

(defn- ticket-proposal-actions [{:keys [id]} tr]
  (let [{:keys [proposal state disabled? is-latest?]} (<sub [::ticket-proposal-ui id])]
    (when proposal
      [:<>
       [:div.mt-2.flex.gap-2
        [ui/button {:on-click #(>evt [::open-ticket-edit-modal (:ticket proposal)])
                    :size     :small
                    :kind     :secondary
                    :disabled disabled?}
         (tr [:chat/edit-ticket])]
        [ui/button {:on-click #(>evt [::confirm-ticket {:ticket (:ticket proposal)}])
                    :size     :small
                    :kind     (if is-latest? :primary :secondary)
                    :disabled disabled?}
         (if (= state :loading)
           (tr [:chat/creating-ticket])
           (tr [:chat/confirm-ticket]))]]])))


(defn- closest-guide-element [target]
  (some-> target (.closest "[data-guide]")))

(defn- bot-message [tr {:keys [text error actions ticket-id] :as msg}]
  [:div.flex.flex-col.items-start.mb-4
   [:div.border.border-solid.border-gray-400.rounded.p-3.max-w-4-5
    {:class (when error "bg-ui-red-10")}
    [:div.ai-chat-message
     (cond
       error
       (tr [error])

       ticket-id
       (tr [:chat/ticket-created] [ticket-id])

       :else
       ;; HTML content has been sanitized on the backend
       {:dangerouslySetInnerHTML (r/unsafe-html text)
        :on-mouse-over           #(when-let [guide-el (closest-guide-element (.-target %))]
                                    (>evt [::highlight (.getAttribute guide-el "data-guide")]))
        :on-mouse-out            #(when-let [guide-el (closest-guide-element (.-target %))]
                                    (when-not (some->> (.-relatedTarget %) (.contains guide-el))
                                      (>evt [::clear-highlight])))})]
    (when (some #(= (:type %) :propose-ticket) actions)
      [ticket-proposal-actions msg tr])]
   [:span.text-white.mt-1.bg-ui-night-sky-100.p-2.rounded bot-name]])

(defn- strip-internal-system-note [text]
  ;; Multiple internal notes can be prepended before a user message; keep them out of the visible chat history.
  (some-> text (str/replace #"^(\[System note:[\s\S]*?\]\n\n)+" "")))

(defn- user-message [{:keys [text]} tr]
  [:div.flex.flex-col.items-end.mb-4
   [:div.border.border-solid.border-gray-400.rounded.p-3.max-w-4-5.bg-ui-light-gray
    [:p.text-sm.whitespace-pre-wrap (strip-internal-system-note text)]]
   [:span.text-white.mt-1.bg-ui-night-sky-100.p-2.rounded (tr [:chat/you])]])

(defn- message-item [{:keys [sender] :as msg} tr]
  (if (= sender :bot)
    [bot-message tr msg]
    [user-message msg tr]))

(defn- displayable-message? [{:keys [sender text error ticket-id]}]
  (or error
      ticket-id
      (not (str/blank? (cond-> text
                         (not= sender :bot) strip-internal-system-note)))))

(defn- messages-list []
  (let [scroll-ref (atom nil)]
    (r/create-class
      {:component-did-update
       (fn [_]
         (when-let [el @scroll-ref]
           (set! (.-scrollTop el) (.-scrollHeight el))))
       :reagent-render
       (fn []
         (let [messages         (<sub [::messages])
               visible-messages (filterv displayable-message? messages)
               tr               (<sub [:tr])
               loading?         (<sub [::loading?])
               history-loading? (<sub [::history-loading?])]
           [:div.flex-1.overflow-y-auto.p-4.bg-white
            {:ref #(reset! scroll-ref %)}
            (cond
              history-loading?
              [:div.flex.justify-center.mt-8
               (cc/inline-loading {:description (tr [:chat/loading-history])})]

              (seq visible-messages)
              (futil/for-frag [msg visible-messages]
                ^{:key (message-key msg)}
                [message-item msg tr])

              :else
              [:p.text-gray-400.text-center.mt-8 (tr [:chat/no-messages])])
            (when loading?
              (cc/inline-loading {:description (str bot-name " is thinking about it...")}))]))})))

(defn- message-input []
  (let [tr            (<sub [:tr])
        message       (<sub [::message])
        forms-context (<sub [::form-subs/forms-step-page-context])
        on-send       #(when (seq message)
                         (>evt [::send-message message forms-context]))]
    [:div.p-4.border-t.border-solid.border-gray-200
     [:textarea.w-full.p-2.border.border-solid.border-gray-300.rounded.resize-none.text-base
      {:id          "chat-panel-input"
       :placeholder (tr [:chat/message-placeholder])
       :value       (or message "")
       :rows        3
       :on-change   #(>evt [::set-message (.. % -target -value)])
       :on-key-down (fn [e]
                      (when (and (= (.-key e) "Enter")
                                 (not (.-shiftKey e)))
                        (.preventDefault e)
                        (on-send)))}]
     [:div.mt-2.flex.justify-end.gap-2
      [ui/button {:on-click #(>evt [::reset-chat])
                  :kind     :secondary
                  :size     :small
                  :icon     i/refresh}
       (tr [:chat/reset])]
      [ui/button {:on-click on-send
                  :size     :small
                  :icon     SendAlt24}
       (tr [:chat/send])]]]))

(defn- chat-panel-body []
  [:div.flex.flex-col.flex-1.min-h-0
   [messages-list]
   [message-input]])

(defn- chat-panel-content []
  (let [tr (<sub [:tr])]
    [:div.flex.flex-col.h-full.py-4
     ;; Header with hide button
     [:div.flex.justify-between.p-2.ml-4
      [cc/icon {:icon i/nova :description "" :class-name "h-12 w-12"}]
      (cc/button {:kind       :ghost
                  :size       :small
                  :class-name "uppercase"
                  :on-click   #(>evt [::toggle-open {:open? false}])}
                 (tr [:chat/hide-chat]) " →")]
     [chat-panel-body]]))

(defn- floating-button []
  (let [tr (<sub [:tr])]
    [:button.fixed.right-0.bottom-40.z-40.w-12.h-12.rounded-l-lg.bg-ui-night-sky-100.text-white.flex.items-center.justify-center.shadow-lg.cursor-pointer.border-0.hover:bg-ui-night-sky-110
     {:on-click #(>evt [::toggle-open {:open? true}])
      :title    (tr [:chat/heading])}
     [cc/icon
      {:icon i/nova :description "chat-icon" :class-name "fill-white"}]]))


;; Resize
(defn- mouse-x->panel-width
  "Clamped panel width from mouse position. 16px offset for `right-4`."
  [client-x]
  (let [inner-width (.-innerWidth js/window)
        max-w       (quot inner-width 2)
        min-w       280]
    (-> (- inner-width client-x 16)
        (max min-w)
        (min max-w))))

(defn- start-drag! [on-drag on-end]
  (let [on-up (fn this [e]
                (.removeEventListener js/window "mousemove" on-drag)
                (.removeEventListener js/window "mouseup" this)
                (on-end e))]
    (.addEventListener js/window "mousemove" on-drag)
    (.addEventListener js/window "mouseup" on-up)))

(defn- resize-handle []
  (r/with-let [dragging? (r/atom false)]
    [:div.absolute.top-0.bottom-0.left-0.rounded-lg
     {:style         {:width "6px" :cursor "col-resize" :z-index 60 :opacity (if @dragging? 1 0)}
      :on-mouse-down (fn [e]
                       (.preventDefault e)
                       (reset! dragging? true)
                       (start-drag! (fn [^js me] (>evt [::set-panel-width (mouse-x->panel-width (.-clientX me))]))
                                    (fn [_] (reset! dragging? false))))}
     [:div.absolute.top-1.bottom-1.left-0.rounded-lg
      {:style {:width            "3px"
               :background-color (if @dragging? "#0f62fe" "#e0e0e0")
               :transition       "background-color 150ms"}}]]))

(defn chat-side-panel
  "Chat in a side-panel wrapper."
  []
  (let [panel-open? (<sub [::panel-open?])
        tr          (<sub [:tr])]
    [side-panel/side-panel {:panel-open? panel-open? :panel-id :chat}
     [side-panel/title (tr [:chat/nova])]
     [chat-panel-body]]))

(defn chat-panel
  "Floating chat panel - button when closed, slide-out panel when open."
  []
  (let [panel-open? (<sub [::panel-open?])
        panel-width (<sub [::panel-width])]
    (if panel-open?
      [:div.fixed.top-48.right-4.bottom-4.max-w-full.bg-white.flex.flex-col.animation-appear-slide-right.rounded-lg
       {:style {:width   (str panel-width "px")
                :z-index 8998}}
       [resize-handle]
       [chat-panel-content]]
      [floating-button])))
